import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { SegmentedControl } from '../../../ui';
import type { CommercialDateMode } from '../../../../utils/sales/commercial-sale-date';
import CommercialDateTimePicker from './CommercialDateTimePicker';

const CREATE_DATE_MODE_OPTIONS = [
  { value: 'current' as const, label: 'Fecha actual', ariaLabel: 'Fecha actual' },
  {
    value: 'custom' as const,
    label: 'Elegir otra fecha',
    ariaLabel: 'Elegir otra fecha',
  },
];

const EDIT_DATE_MODE_OPTIONS = [
  {
    value: 'keep' as const,
    label: 'Mantener fecha',
    ariaLabel: 'Mantener fecha registrada',
  },
  ...CREATE_DATE_MODE_OPTIONS,
];

export interface CommercialDateModeSectionProps {
  /** `null` until the user explicitly picks an option (create). Edit may start as `keep`. */
  mode: CommercialDateMode | null;
  onModeChange: (mode: CommercialDateMode) => void;
  value: Date;
  onChange: (value: Date) => void;
  title?: string;
  pickerLabel: string;
  currentHelperText?: string;
  keepHelperText?: string;
  groupAriaLabel?: string;
  disabled?: boolean;
  /** Highlight the control when confirm was attempted without a choice. */
  error?: boolean;
  /** Show "Mantener fecha" for post-sale commercial edits. */
  allowKeep?: boolean;
}

/**
 * Exclusive choice for commercial datetime.
 * Create: current vs custom (must choose). Edit: keep / current / custom.
 */
const CommercialDateModeSection: React.FC<CommercialDateModeSectionProps> = ({
  mode,
  onModeChange,
  value,
  onChange,
  title = 'Fecha y hora',
  pickerLabel,
  currentHelperText = 'Se usará la fecha y hora en el momento de confirmar.',
  keepHelperText = 'Se conservará la fecha y hora comercial ya registrada.',
  groupAriaLabel = 'Fecha y hora comercial',
  disabled = false,
  error = false,
  allowKeep = false,
}) => {
  const options = useMemo(
    () => (allowKeep ? EDIT_DATE_MODE_OPTIONS : CREATE_DATE_MODE_OPTIONS),
    [allowKeep],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="body2"
        sx={{
          mb: 0.75,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: error ? 'error.main' : 'text.primary',
        }}
      >
        {title}
      </Typography>
      <SegmentedControl
        value={mode}
        options={options}
        onChange={onModeChange}
        ariaLabel={groupAriaLabel}
        fullWidth
        size="small"
        disabled={disabled}
        error={error}
      />
      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
          Seleccione una opción
        </Typography>
      ) : null}

      {mode === 'keep' ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}
        >
          {keepHelperText}
        </Typography>
      ) : null}

      {mode === 'current' ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}
        >
          {currentHelperText}
        </Typography>
      ) : null}

      {mode === 'custom' ? (
        <Box sx={{ mt: 1.25 }}>
          <CommercialDateTimePicker
            label={pickerLabel}
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default CommercialDateModeSection;
